import { Access } from "@p0security/shared/types/access/index";

/** The roles a team membership can have, as the connector names them. */
export const GITHUB_TEAM_ROLES = ["member", "maintainer"] as const;

export const GithubAccesses = {
  role: Access({
    description: "A direct collaborator role on a GitHub repository",
    label: "Repository role",
    maturity: "beta",
    delegation: {},
    generated: {
      // The user's direct role on the repository when the request was staged,
      // in the form GitHub's collaborator PUT takes. Absent when they had
      // none. Revoking restores it, or removes the direct role if absent.
      priorRole: {
        type: "string",
        label: "Prior role",
        description: "The direct role restored when this access ends",
        optional: true,
      },
      // The grantee's GitHub user id: the numeric id behind their login. A
      // login can be renamed and then claimed by someone else, so the
      // connector acts on the account.
      githubUserId: { type: "string", label: "GitHub user ID" },
      // The repository's GitHub id. A repository can be renamed, so P0 tells
      // two requests are for one repository by its id. Absent on a request
      // staged before P0 recorded it, or when the connector couldn't look it
      // up. P0 matches those by name.
      repositoryId: {
        type: "string",
        label: "GitHub repository ID",
        optional: true,
      },
    },
    parameters: {
      // The full name, `<org>/<repo>`: bare repository names are only unique
      // within one organization. The description says `{org}/{repo}` because
      // Slack shows `<` and `>` in a placeholder as `&lt;` and `&gt;`.
      repository: {
        type: "dynamic",
        label: "Repository",
        description: "GitHub repository, as {org}/{repo}",
      },
      role: {
        type: "dynamic",
        label: "Role",
        description: "Repository role to grant",
      },
      username: {
        type: "generated",
        label: "GitHub username",
        description: "The requestor's login in the organization",
      },
    },
    render: { locator: "repository", policy: "role" } as const,
    // GitHub gives a person one role per repository
    singleValued: { parameter: "role", per: "repository" },
  }),
  team: Access({
    description: "Membership of a GitHub team",
    label: "Team membership",
    maturity: "beta",
    delegation: {},
    generated: {
      // The user's own membership of the team when the request was staged,
      // `member` or `maintainer`. Absent when they weren't on it. Revoking
      // restores it, or takes them off the team if absent.
      priorRole: {
        type: "string",
        label: "Prior role",
        description: "The team role restored when this access ends",
        optional: true,
      },
      // The grantee's GitHub user id, as on the `role` access.
      githubUserId: { type: "string", label: "GitHub user ID" },
    },
    parameters: {
      // The full name, `<org>/<team>`, where `<team>` is the team's slug: like
      // a repository's name, a slug is only unique within one organization.
      team: {
        type: "dynamic",
        label: "Team",
        description: "GitHub team, as {org}/{team}",
      },
      role: {
        type: "static",
        label: "Role",
        description: "Team role to grant",
        choices: GITHUB_TEAM_ROLES,
      },
      // The team's GitHub id, looked up when access is requested. A team's
      // maintainers can rename it, and another team can then take its old
      // slug, so P0 acts on the team that was requested by its id.
      teamId: {
        type: "generated",
        label: "GitHub team ID",
        description: "The id of the requested team",
      },
      username: {
        type: "generated",
        label: "GitHub username",
        description: "The requestor's login in the organization",
      },
    },
    render: { locator: "team", policy: "role" } as const,
  }),
  "force-merge": Access({
    description:
      "Merging into a protected GitHub branch without its required reviews",
    label: "Force merge",
    maturity: "beta",
    delegation: {},
    generated: {
      // Whether the user was on the branch's list of who can bypass its
      // required reviews when the request was staged, `true` or `false`.
      // Revoking leaves them on it if so, and takes them off it otherwise.
      priorAllowed: {
        type: "string",
        label: "Prior bypass",
        description: "Whether the user could bypass reviews before this access",
        choices: ["true", "false"],
      },
      // The grantee's GitHub user id, as on the `role` access.
      githubUserId: { type: "string", label: "GitHub user ID" },
    },
    parameters: {
      // Picked first in the request form, which then lists only this
      // repository's protected branches. The full name, `<org>/<repo>`, as on
      // the `role` access. Optional, since the branch names its repository
      // too: a request records the branch's.
      repository: {
        type: "dynamic",
        label: "Repository",
        description:
          "GitHub repository whose branches to list, as {org}/{repo}",
        optional: true,
      },
      // The branch's full name, `<org>/<repo>/<branch>`. A branch's name can
      // hold `/`, so everything after the repository is the branch.
      branch: {
        type: "dynamic",
        label: "Branch",
        description: "Protected GitHub branch, as {org}/{repo}/{branch}",
      },
      username: {
        type: "generated",
        label: "GitHub username",
        description: "The requestor's login in the organization",
      },
    },
    render: { locator: "branch" } as const,
  }),
};

export type GithubAccesses = typeof GithubAccesses;
export type GithubAccess = GithubAccesses[keyof GithubAccesses];
export type GithubRoleAccess = GithubAccesses["role"];
export type GithubTeamAccess = GithubAccesses["team"];
export type GithubForceMergeAccess = GithubAccesses["force-merge"];
